import { useCallback } from 'react';
import { Link } from '~/components/link';
import { useLink } from '~/hooks/link';
import { useLocation } from '~/hooks/location';
import { exhausted } from '~/lib/assert';

interface Entities {
  ContentFolder: unknown;
  ContentItem: { contentFolderId?: number | null };
  DataSource: unknown;
  Design: { designFolderId: number };
  DesignFolder: unknown;
  Device: unknown;
  DeviceGroupNew: unknown;
  EventType: unknown;
  MediaItem: { contentFolderId: number };
  Playlist: { playlistFolderId: number };
  PlaylistFolder: unknown;
  Show: { showFolderId: number };
  ShowFolder: unknown;
  User: unknown;
}

type NamedEntity = {
  [K in keyof Entities]: { __typename: K; id: number; name: string } & Entities[K];
}[keyof Entities];

export const pathFor = ({ id, ...entity }: NamedEntity): string => {
  if (entity.__typename === 'ContentFolder') return `/content/${id}`;
  if (entity.__typename === 'ContentItem')
    return `/content/${entity.contentFolderId}/apps/${id}/settings`;
  if (entity.__typename === 'DataSource') return `/data-sources/${id}`;
  if (entity.__typename === 'Design') return `/studio/${entity.designFolderId}/design/${id}`;
  if (entity.__typename === 'DesignFolder') return `/studio/${id}`;
  if (entity.__typename === 'Device') return `/devices/${id}`;
  if (entity.__typename === 'DeviceGroupNew') return `/device-groups/${id}`;
  if (entity.__typename === 'EventType') return `/event-types/${id}`;
  if (entity.__typename === 'MediaItem')
    return `/content/${entity.contentFolderId}/media/${id}/details`;
  if (entity.__typename === 'Playlist')
    return `/playlists/${entity.playlistFolderId}/playlist/${id}`;
  if (entity.__typename === 'PlaylistFolder') return `/playlists/${id}`;
  if (entity.__typename === 'Show') return `/shows/${entity.showFolderId}/show/${id}`;
  if (entity.__typename === 'ShowFolder') return `/shows/${id}`;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (entity.__typename === 'User') return `/users/${id}`;
  return exhausted(entity, 'pathFor: unknown entity');
};

interface AuthorizedLinkProps {
  authorized?: boolean;
  entity: NamedEntity | undefined | null;
  fallback?: string;
  children?: React.ReactNode;
}

/**
 * AuthorizedLink
 *
 * Renders a link to an entity's detail page only when the user is authorized
 * **and** an entity is present. Otherwise, it renders plain content without a link.
 *
 * Priority for displayed content:
 *   1) children (if provided)
 *   2) entity.name (if present)
 *   3) fallback (defaults to `"--"`)
 */
export const AuthorizedLink = ({
  authorized = false,
  entity,
  fallback = '--',
  children,
}: AuthorizedLinkProps) => {
  const link = useLink();
  const location = useLocation();

  // Stop the table row's onClick from firing when the link is clicked.
  // This keeps row-level handlers (e.g., row selection/navigation) from triggering.
  const handleClick = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

  // Only clickable if we have both permission and a target entity.
  const isClickable = authorized && !!entity;

  // Decide what to show inside the link/span.
  // Use children if present; otherwise fall back to entity name or the provided fallback.
  const content = children ?? entity?.name ?? fallback;

  // If clickable, render a real <Link>; otherwise:
  // - If children were provided, return them untouched (no extra wrapper).
  // - If not, render a <span> so layout remains stable.
  // The explicit tabindex is for data grid cells: the grid hands a cell's focus to a child
  // with tabindex="0", so the arrow keys land on the link and Enter follows it.
  return isClickable ? (
    <Link
      to={link(pathFor(entity))}
      onClick={handleClick}
      state={{ returnTo: location.fullPath }}
      tabIndex={0}
    >
      {content}
    </Link>
  ) : children ? (
    <>{children}</>
  ) : (
    <span>{content}</span>
  );
};
